import {Plus} from 'lucide-react';
import {formatDate} from '@common/lib/utils';
import {DataTable, type Column} from '@common-web/components/data-table';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {MemberStatusBadge} from '@client/pages/client/member-profile/MemberStatusBadge';

export type MemberProductRow = {
  id: string;
  name: string;
  category: string | null;
  fee: number | null;
  status: 'Active' | 'Inactive';
  activeDate: string | null;
  inactiveDate: string | null;
  planId: string;
  memberSeq: string;
  memberName: string;
};

type MemberProductsTableProps = {
  products: MemberProductRow[];
  unavailableMessage?: string;
};

function formatFee(fee: number | null): string {
  if (fee == null || Number.isNaN(fee)) return '—';
  return `$${fee.toFixed(2)}`;
}

const INTERNAL_HEADERS = [
  {label: 'Name'},
  {label: 'Category'},
  {label: 'Fee', numeric: true},
  {label: 'Status'},
  {label: 'Active Date'},
  {label: 'Inactive Date'},
] as const;

/** Demo-parity products card body for the internal portal: bordered header row,
 *  the bundle-rule banner, a full-bleed table with a right-aligned fee column,
 *  and a rich empty state. */
function InternalProductsTable({products, unavailableMessage}: MemberProductsTableProps) {
  return (
    <div>
      <div className='flex items-center justify-between gap-3 border-b px-4 py-3'>
        <h3 className='text-[13px] font-semibold text-foreground'>Products</h3>
      </div>
      {/* Bundle rule — mirrors CHAMP: the medical product never rides alone. */}
      <div className='border-b bg-primary/5 px-4 py-2.5 text-[12px] text-muted-foreground'>
        Adding or dropping coverage automatically includes the Section 125 Plan and the Admin Fee
        alongside the medical product. A member can only have one medical product active at a time.
      </div>
      {products.length === 0 ? (
        <div className='flex flex-col items-center gap-1.5 px-6 py-10 text-center'>
          <Plus className='h-5 w-5 text-muted-foreground' />
          <p className='text-[13px] font-medium text-foreground'>
            {unavailableMessage ?? 'No products assigned to this member.'}
          </p>
          {!unavailableMessage && (
            <p className='max-w-md text-[12px] text-muted-foreground'>
              Products drive coverage and commissions — enroll the member in one of the group&apos;s
              products.
            </p>
          )}
        </div>
      ) : (
        <table className='w-full'>
          <thead>
            <tr className='bg-muted/50'>
              {INTERNAL_HEADERS.map((h) => (
                <th
                  key={h.label}
                  className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${
                    'numeric' in h && h.numeric ? 'text-right' : 'text-left'
                  }`}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.map((row) => (
              <tr key={row.id} className='border-b border-gray-100 last:border-b-0'>
                <td className='px-4 py-3'>
                  <div className='text-[13px] font-medium text-foreground'>{row.name}</div>
                  <div className='text-xs text-muted-foreground'>{row.memberName}</div>
                </td>
                <td className='px-4 py-3 text-[13px] text-muted-foreground'>
                  {row.category ?? '—'}
                </td>
                <td className='px-4 py-3 text-right text-[13px] tabular-nums text-muted-foreground'>
                  {formatFee(row.fee)}
                </td>
                <td className='px-4 py-3 text-[13px]'>
                  <MemberStatusBadge
                    status={row.status === 'Active' ? 'active' : 'inactive'}
                    label={row.status}
                    tone='product'
                    size='compact'
                  />
                </td>
                <td className='px-4 py-3 text-[13px] text-muted-foreground'>
                  {formatDate(row.activeDate, 'MMM D, YYYY', '—')}
                </td>
                <td className='px-4 py-3 text-[13px] text-muted-foreground'>
                  {formatDate(row.inactiveDate, 'MMM D, YYYY', '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function MemberProductsTable({products, unavailableMessage}: MemberProductsTableProps) {
  const isInternal = useIsInternal();

  if (isInternal) {
    return <InternalProductsTable products={products} unavailableMessage={unavailableMessage} />;
  }

  const columns: Column<MemberProductRow>[] = [
    {
      id: 'name',
      header: 'Name',
      render: (row) => (
        <div className='min-w-0'>
          <div className='font-medium text-brand-navy'>{row.name}</div>
          <div className='text-xs text-brand-gray'>{row.memberName}</div>
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      render: (row) => row.category ?? '—',
    },
    {
      id: 'fee',
      header: 'Fee',
      render: (row) => <span className='tabular-nums'>{formatFee(row.fee)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => (
        <MemberStatusBadge
          status={row.status === 'Active' ? 'active' : 'inactive'}
          tone='product'
        />
      ),
    },
    {
      id: 'activeDate',
      header: 'Active Date',
      render: (row) => formatDate(row.activeDate, 'MMM D, YYYY', '—'),
    },
    {
      id: 'inactiveDate',
      header: 'Inactive Date',
      render: (row) => formatDate(row.inactiveDate, 'MMM D, YYYY', '—'),
    },
  ];

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-xs font-semibold uppercase tracking-wide text-brand-gray'>Products</h3>
      </div>
      <DataTable
        columns={columns}
        data={products}
        rowKey={(row) => row.id}
        emptyMessage={unavailableMessage ?? 'No products on file.'}
      />
    </div>
  );
}
